import type { VideoPlayerConfig } from "@memorang/types";
import {
	MediaCaptionsButton,
	MediaControlBar,
	MediaController,
	MediaFullscreenButton,
	MediaLoadingIndicator,
	MediaMuteButton,
	MediaPlayButton,
	MediaTimeDisplay,
	MediaTimeRange,
} from "media-chrome/react";
import {
	forwardRef,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from "react";
import { themeRoundnessMultiplier } from "../../constants";
import { useAppTheme } from "../../useAppTheme";

export type SessionVideoPlayerRef = {
	play: () => Promise<void>;
	pause: () => void;
	isEnded: boolean;
	isPlaying: boolean;
	isLoaded: boolean;
	seekTo: (timeMs: number) => void;
};

type Props = {
	uri: string;
	autoPlay?: boolean;
	playOnce?: boolean;
	videoPlayerConfig?: VideoPlayerConfig;
	progressSeekable?: boolean;
};

const MAX_VIDEO_WIDTH = 640;

export const SessionVideoPlayer = forwardRef<SessionVideoPlayerRef, Props>(
	(
		{ uri, autoPlay, playOnce, videoPlayerConfig, progressSeekable = false },
		ref,
		// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: na
	) => {
		const allowPlayPause = videoPlayerConfig?.allowVideoPlayPause ?? true;
		const showProgress = videoPlayerConfig?.showVideoProgress ?? true;
		const allowVolumeControl =
			videoPlayerConfig?.allowVideoVolumeControl ?? true;
		const allowCaptions = videoPlayerConfig?.allowVideoCaptions ?? false;
		const allowFullscreen = videoPlayerConfig?.allowVideoFullscreen ?? true;
		const theme = useAppTheme();
		const videoRef = useRef<HTMLVideoElement | null>(null);

		const [isPlaying, setIsPlaying] = useState(false);
		const [isEnded, setIsEnded] = useState(false);
		const [isLoaded, setIsLoaded] = useState(false);
		const hasAutoPlayed = useRef(false);

		useEffect(() => {
			const video = videoRef.current;
			if (!video) {
				return;
			}
			const handleLoaded = () => setIsLoaded(true);
			const handlePlaying = () => {
				setIsPlaying(true);
				setIsEnded(false);
			};
			const handlePause = () => setIsPlaying(false);
			const handleEnded = () => {
				setIsEnded(true);
				setIsPlaying(false);
			};
			video.addEventListener("canplay", handleLoaded);
			video.addEventListener("playing", handlePlaying);
			video.addEventListener("pause", handlePause);
			video.addEventListener("ended", handleEnded);
			return () => {
				video.removeEventListener("canplay", handleLoaded);
				video.removeEventListener("playing", handlePlaying);
				video.removeEventListener("pause", handlePause);
				video.removeEventListener("ended", handleEnded);
			};
		}, []);

		useEffect(() => {
			if (autoPlay && isLoaded && !hasAutoPlayed.current) {
				hasAutoPlayed.current = true;
				videoRef.current?.play();
			}
		}, [autoPlay, isLoaded]);

		useImperativeHandle(
			ref,
			() => ({
				play: () => videoRef.current?.play() ?? Promise.resolve(),
				pause: () => {
					videoRef.current?.pause();
				},
				isEnded,
				isPlaying,
				isLoaded,
				seekTo: (timeMs: number) => {
					if (videoRef.current) {
						videoRef.current.currentTime = timeMs / 1000;
					}
				},
			}),
			[isEnded, isPlaying, isLoaded],
		);

		const showControlBar = !(playOnce && isEnded);
		const borderRadius = theme.roundness * themeRoundnessMultiplier;

		return (
			<MediaController
				style={{
					display: "block",
					width: "100%",
					maxWidth: MAX_VIDEO_WIDTH,
					marginInline: "auto",
					aspectRatio: "16 / 9",
					borderRadius,
					overflow: "hidden",
					border: 0,
					outline: "none",
					backgroundColor: "transparent",
				}}
			>
				{/* biome-ignore lint/a11y/useMediaCaption: session stimulus has no caption track */}
				<video
					slot="media"
					ref={videoRef}
					src={uri}
					preload="auto"
					style={{
						width: "100%",
						height: "100%",
						objectFit: "cover",
						display: "block",
						border: 0,
						outline: "none",
					}}
				/>
				<MediaLoadingIndicator slot="centered-chrome" />
				{showControlBar ? (
					<MediaControlBar
						style={{
							position: "absolute",
							bottom: 0,
							left: 0,
							width: "100%",
						}}
					>
						{allowPlayPause ? <MediaPlayButton /> : null}
						{showProgress ? (
							progressSeekable ? (
								<MediaTimeRange />
							) : (
								<MediaTimeRange
									ref={(el) => el?.setAttribute("disabled", "")}
								/>
							)
						) : null}
						<MediaTimeDisplay showDuration />
						{allowVolumeControl ? <MediaMuteButton /> : null}
						{allowCaptions ? <MediaCaptionsButton /> : null}
						{allowFullscreen ? <MediaFullscreenButton /> : null}
					</MediaControlBar>
				) : null}
			</MediaController>
		);
	},
);
